// ----- Phase 5.X-document-handling-enhancements (Feature 1) — Signature badge -----
//
// Small visual badge rendered next to a document's FileName indicating
// whether the PDF carries a digital signature, a visual ("wet")
// signature, or neither.
//
// `null` and `'none'` BOTH render nothing — the absence of a badge is
// the canonical "unsigned" state. Distinguishing the two would require
// a third icon (e.g., greyed) which V1 didn't have and which would clutter
// the doc list at scale.
//
// Per guardrail #4 the wet-signature label is intentionally hedged
// ("Wet signature (heuristic)" rather than just "Wet signature") so
// users with bad PDF imports don't take it as a compliance signal.

import type { CSSProperties, ReactElement } from 'react';

export type SignatureStatusValue = 'digital' | 'wet' | 'none' | string | null | undefined;

export interface SignatureBadgeProps {
  readonly status: SignatureStatusValue;
  /** Optional override for the inline-block CSS — lets callers tune the
   *  badge's spacing inside doc rows vs inspector headers. */
  readonly style?: CSSProperties;
}

const baseBadgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '1px 6px',
  borderRadius: 4,
  fontSize: 10,
  fontWeight: 600,
  lineHeight: 1.4,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
};

const digitalBadgeStyle: CSSProperties = {
  ...baseBadgeStyle,
  background: '#dcfce7',
  color: '#15803d',
  border: '1px solid #86efac',
};

const wetBadgeStyle: CSSProperties = {
  ...baseBadgeStyle,
  background: '#fef3c7',
  color: '#92400e',
  border: '1px solid #fcd34d',
};

export function SignatureBadge({ status, style }: SignatureBadgeProps): ReactElement | null {
  if (status === 'digital') {
    return (
      <span
        style={{ ...digitalBadgeStyle, ...style }}
        data-testid="signature-badge-digital"
        title="PDF carries a cryptographic /Sig field (DocuSign / Adobe Sign / native PDF reader)"
        aria-label="Digitally signed"
      >
        🔏 Digitally signed
      </span>
    );
  }
  if (status === 'wet') {
    return (
      <span
        style={{ ...wetBadgeStyle, ...style }}
        data-testid="signature-badge-wet"
        title="Heuristic detection — last page has an embedded image where a wet signature typically sits. May be a false positive (logo, letterhead)."
        aria-label="Wet signature (heuristic)"
      >
        ✍️ Wet signature
      </span>
    );
  }
  // 'none' or null or anything else → render nothing. Absence of badge is
  // the canonical unsigned state.
  return null;
}
